import buildPositionKey from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/buildPositionKey';
import parseInstancePositions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/parseInstancePositions';
import type DocumentListEntryView from '@UI/Store/Documents/Views/DocumentListEntryView';

export type FieldPlacement = {
	readonly uuid: string;
	/** Index 0-based of each list element walked through, outermost first; `[]` outside any list. */
	readonly positions: readonly number[];
};

export type ListTarget = {
	readonly list: DocumentListEntryView;
	/** The element the viewer path names; `null` when the path names the list itself. */
	readonly index: number | null;
};

/**
 * Immutable lookup between what the viewer reports (an uuid and an instance path) and what Dixit keys
 * (the opaque field key, the list entry). Built once per document by `buildViewerFieldIndex`; the front
 * never composes a Dixit key itself.
 */
class ViewerFieldIndex {
	private readonly fieldKeys: ReadonlyMap<string, string>;
	private readonly lists: ReadonlyMap<string, DocumentListEntryView>;
	private readonly placements: ReadonlyMap<string, FieldPlacement>;
	private readonly listPlacements: ReadonlyMap<string, FieldPlacement>;

	constructor(
		fieldKeys: ReadonlyMap<string, string>,
		lists: ReadonlyMap<string, DocumentListEntryView>,
		placements: ReadonlyMap<string, FieldPlacement>,
		listPlacements: ReadonlyMap<string, FieldPlacement>,
	) {
		this.fieldKeys = fieldKeys;
		this.lists = lists;
		this.placements = placements;
		this.listPlacements = listPlacements;

		Object.freeze(this);
	}

	/** The Dixit key of the field with this uuid at these list positions. */
	findFieldKey(uuid: string, positions: readonly number[]): string | null {
		return this.fieldKeys.get(buildPositionKey(uuid, positions)) ?? null;
	}

	/** Where a Dixit field sits, `null` for an unknown key. */
	findPlacement(fieldKey: string): FieldPlacement | null {
		return this.placements.get(fieldKey) ?? null;
	}

	/** Where a Dixit list sits, by its opaque key: `positions` are those of the elements that contain it. */
	findListPlacement(listKey: string): FieldPlacement | null {
		return this.listPlacements.get(listKey) ?? null;
	}

	/** The list with this uuid inside the elements at `containerPositions` (`[]` for a top-level list). */
	findList(listId: string, containerPositions: readonly number[]): DocumentListEntryView | null {
		return this.lists.get(buildPositionKey(listId, containerPositions)) ?? null;
	}

	/**
	 * Resolves what a viewer list event names. `pathNamesItem` is `true` for the instance path of an
	 * element (its last position is the element, the others its containers) and `false` for the
	 * instance path of a list (every position is a container).
	 */
	resolveListTarget(
		listId: string,
		instancePath: string | undefined,
		pathNamesItem: boolean,
	): ListTarget | null {
		const positions = parseInstancePositions(instancePath);

		if (!pathNamesItem) {
			const list = this.findList(listId, positions);
			return list === null ? null : { list, index: null };
		}

		const index = positions.at(-1);

		if (index === undefined) {
			return null;
		}

		const list = this.findList(listId, positions.slice(0, -1));

		return list === null ? null : { list, index };
	}
}

export default ViewerFieldIndex;
